import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiChevronLeft, FiChevronRight, FiPackage, FiTrendingUp, FiFilter, FiDownload, FiEye, FiFileText, FiPercent, FiX } from 'react-icons/fi';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import html2pdf from 'html2pdf.js';

const BanquetManagement = () => {
  const [banquets, setBanquets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanquet, setEditingBanquet] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [stats, setStats] = useState({
    totalPackages: 0,
    avgPricePerPack: 0,
    totalRevenue: 0,
    avgGstPercentage: 0
  });

  const [formData, setFormData] = useState({
    banquetName: '',
    customerName: '',
    gstNumber: '',
    noOfPacks: '',
    pricePerPack: '',
    gstPercentage: '',
    discount: 0,
    discountType: 'percentage'
  });

  useEffect(() => {
    fetchBanquets();
    fetchStats();
  }, [currentPage, searchTerm]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/vendor/banquets/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBanquets = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${searchTerm}` : '';
      const response = await api.get(`/vendor/banquets?page=${currentPage}&limit=10${searchParam}`);
      if (response.data.success) {
        setBanquets(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching banquets:', error);
      toast.error('Failed to fetch banquets');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const noOfPacks = parseFloat(formData.noOfPacks) || 0;
    const pricePerPack = parseFloat(formData.pricePerPack) || 0;
    const subtotal = noOfPacks * pricePerPack;
    
    // Calculate discount on subtotal first
    const discountAmount = formData.discountType === 'percentage' 
      ? (subtotal * parseFloat(formData.discount || 0)) / 100 
      : parseFloat(formData.discount || 0);
    const afterDiscount = subtotal - discountAmount;
    
    // Calculate GST on amount after discount
    const gstAmount = (afterDiscount * parseFloat(formData.gstPercentage || 0)) / 100;
    const total = afterDiscount + gstAmount;

    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.banquetName || !formData.customerName || !formData.noOfPacks || !formData.pricePerPack || !formData.gstPercentage || formData.discount === '') {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate numeric values
    const noOfPacks = parseFloat(formData.noOfPacks);
    const pricePerPack = parseFloat(formData.pricePerPack);
    const gstPercentage = parseFloat(formData.gstPercentage);
    const discount = parseFloat(formData.discount);
    
    if (isNaN(noOfPacks) || noOfPacks < 1) {
      toast.error('Number of packs must be at least 1');
      return;
    }
    
    if (isNaN(pricePerPack) || pricePerPack < 0) {
      toast.error('Price per pack must be a valid positive number');
      return;
    }
    
    if (isNaN(gstPercentage) || gstPercentage < 0 || gstPercentage > 100) {
      toast.error('GST percentage must be between 0 and 100');
      return;
    }
    
    if (isNaN(discount) || discount < 0) {
      toast.error('Discount must be a valid positive number');
      return;
    }
    
    try {
      const totals = calculateTotals();

      const payload = {
        banquetName: formData.banquetName,
        customerName: formData.customerName,
        gstNumber: formData.gstNumber,
        noOfPacks: parseFloat(formData.noOfPacks),
        pricePerPack: parseFloat(formData.pricePerPack),
        gstPercentage: parseFloat(formData.gstPercentage),
        discount: parseFloat(formData.discount),
        discountType: formData.discountType,
        discountAmount: parseFloat(totals.discountAmount),
        subtotal: parseFloat(totals.subtotal),
        gstAmount: parseFloat(totals.gstAmount),
        total: parseFloat(totals.total)
      };

      if (editingBanquet) {
        await api.put(`/vendor/banquets/${editingBanquet._id}`, payload);
        toast.success('Banquet updated successfully');
      } else {
        await api.post('/vendor/banquets', payload);
        toast.success('Banquet created successfully');
      }

      fetchBanquets();
      fetchStats();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving banquet:', error);
      toast.error('Failed to save banquet');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this banquet?')) {
      try {
        await api.delete(`/vendor/banquets/${id}`);
        toast.success('Banquet deleted successfully');
        fetchBanquets();
        fetchStats();
      } catch (error) {
        console.error('Error deleting banquet:', error);
        toast.error('Failed to delete banquet');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      banquetName: '',
      customerName: '',
      gstNumber: '',
      noOfPacks: '',
      pricePerPack: '',
      gstPercentage: '',
      discount: 0,
      discountType: 'percentage'
    });
    setEditingBanquet(null);
  };

const handleExport = () => {
  try {
    // Create CSV content
    const headers = ['Bill No', 'Customer Name', 'GST Number', 'Banquet Name', 'Package #', 'Price/Pack', 'Subtotal', 'Discount Type', 'Discount', 'Discount Amount', 'Amount After Discount', 'GST %', 'GST Amount', 'Total', 'Created Date'];
    const csvContent = [
      headers.join(','),
      ...banquets.map(banquet => {
        const subtotal = parseFloat(banquet.subtotal || 0);
        const discountAmount = parseFloat(banquet.discountAmount || 0);
        const amountAfterDiscount = subtotal - discountAmount;
        
        return [
          banquet.billNo,
          banquet.customerName || 'N/A',
          banquet.gstNumber || 'N/A',
          banquet.banquetName || 'N/A',
          banquet.noOfPacks,
          banquet.pricePerPack,
          banquet.subtotal,
          banquet.discountType,
          banquet.discount,
          banquet.discountAmount || 0,
          amountAfterDiscount.toFixed(2),
          banquet.gstPercentage,
          banquet.gstAmount,
          banquet.total,
          new Date(banquet.createdAt).toLocaleDateString('en-IN')
        ].join(',');
      })
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `banquet_bills_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Data exported successfully!');
  } catch (error) {
    console.error('Export error:', error);
    toast.error('Failed to export data');
  }
};

const handleDownloadBill = async (banquet) => {
  try {

    const vendorResponse = await api.get("/vendor/profile");
    const vendor = vendorResponse.data.data;

    // Function to capitalize each word (title case)
    const capitalizeEachWord = (str) => {
      if (!str) return str;
      return str.replace(/\b\w/g, char => char.toUpperCase());
    };

    const billHTML = `
      <div style="font-family: Courier New, monospace; width:80mm; padding:5mm; font-size:11px; line-height:1.4">

        <div style="text-align:center;margin-bottom:10px">
          <h2 style="font-size:18px;font-weight:bold">
            ${capitalizeEachWord(vendor.restaurantDetails?.restaurantName || "HUKAM MERE AAKA")}
          </h2>

          <div style="font-size:10px">
            ${vendor.restaurantDetails?.address || ""}
          </div>

          <div style="font-size:9px;color:#555">
            ${vendor.restaurantDetails?.gstNumber ? `GST: ${vendor.restaurantDetails.gstNumber.toUpperCase()}` : ""}
            ${vendor.restaurantDetails?.fssaiNumber ? ` | FSSAI: ${vendor.restaurantDetails.fssaiNumber.toUpperCase()}` : ""}
          </div>
        </div>

        <div style="border-bottom:1px dashed #000;padding-bottom:8px;margin-bottom:8px;font-size:10px">
          <div style="display:flex;justify-content:space-between">
            <span><b>Bill #</b></span>
            <span>${banquet.billNo}</span>
          </div>

          <div style="display:flex;justify-content:space-between">
            <span><b>Customer</b></span>
            <span>${banquet.customerName || 'N/A'}</span>
          </div>

          ${
            banquet.gstNumber
              ? `<div style="display:flex;justify-content:space-between">
                  <span><b>Customer GST</b></span>
                  <span>${banquet.gstNumber}</span>
                </div>`
              : ""
          }

          <div style="display:flex;justify-content:space-between">
            <span><b>Banquet</b></span>
            <span>${banquet.banquetName || 'N/A'}</span>
          </div>

          <div style="display:flex;justify-content:space-between">
            <span><b>Date</b></span>
            <span>
              ${new Date(banquet.createdAt).toLocaleString("en-IN")}
            </span>
          </div>

          ${
            vendor.personalDetails?.contactNumber
              ? `<div style="display:flex;justify-content:space-between">
                  <span><b>Phone</b></span>
                  <span>${vendor.personalDetails.contactNumber}</span>
                </div>`
              : ""
          }
        </div>

        <table style="width:100%;border-collapse:collapse;font-size:10px">

          <thead>
            <tr style="border-bottom:2px solid black">
              <th align="left">ITEM</th>
              <th align="right">QTY</th>
              <th align="right">PRICE</th>
              <th align="right">TOTAL</th>
            </tr>
          </thead>

          <tbody>

            <tr style="border-bottom:1px dashed #ccc">
              <td>Banquet Package #${banquet.noOfPacks}</td>
              <td align="right">${banquet.noOfPacks}</td>
              <td align="right">₹${banquet.pricePerPack}</td>
              <td align="right">₹${banquet.subtotal}</td>
            </tr>

          </tbody>
        </table>

        <div style="margin-top:10px;border-top:2px solid #000;padding-top:8px;font-size:11px">

          <div style="display:flex;justify-content:space-between">
            <span>Subtotal</span>
            <span>₹${Number(banquet.subtotal).toFixed(2)}</span>
          </div>

          ${
            banquet.discountAmount > 0
              ? `<div style="display:flex;justify-content:space-between;color:#16a34a">
                  <span>Discount</span>
                  <span>- ₹${Number(banquet.discountAmount).toFixed(2)}</span>
                </div>`
              : ""
          }

          <div style="display:flex;justify-content:space-between">
            <span>CGST</span>
            <span>₹${(Number(banquet.gstAmount) / 2).toFixed(2)}</span>
          </div>

          <div style="display:flex;justify-content:space-between">
            <span>SGST</span>
            <span>₹${(Number(banquet.gstAmount) / 2).toFixed(2)}</span>
          </div>

          <div style="display:flex;justify-content:space-between;font-weight:bold;border-top:2px solid #000;margin-top:6px;padding-top:6px;font-size:13px">
            <span>GRAND TOTAL</span>
            <span>₹${Number(banquet.total).toFixed(2)}</span>
          </div>

        </div>

        <div style="text-align:center;margin-top:15px;font-size:10px;border-top:1px dashed #000;padding-top:8px">
          <p>Thank you for your business!</p>
          ${
            vendor.personalDetails?.contactNumber
              ? `<p>Contact: ${vendor.personalDetails.contactNumber}</p>`
              : ""
          }
          <p>Generated ${new Date().toLocaleString("en-IN")}</p>
        </div>

      </div>
    `;

    const element = document.createElement("div");
    element.innerHTML = billHTML;
    document.body.appendChild(element);

    const opt = {
      margin: 0,
      filename: `${banquet.billNo}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 3 },
      jsPDF: {
        unit: "mm",
        format: [80, 200],
        orientation: "portrait",
      },
    };

    await html2pdf().set(opt).from(element).save();

    document.body.removeChild(element);

    toast.success("Bill downloaded successfully!");

  } catch (error) {
    console.error(error);
    toast.error("Failed to generate bill");
  }
};



  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  const totals = calculateTotals();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FiPackage className="text-blue-600" />
              Banquet Management
            </h1>
            <p className="text-gray-600 mt-2">Manage banquet pricing packages and calculations</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload />
              Export
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <FiPlus />
              Add Bill
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Packages</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPackages}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <FiPackage className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Price/Pack</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{parseFloat(stats.avgPricePerPack).toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <span className="text-green-600 text-xl font-bold">₹</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{parseFloat(stats.totalRevenue).toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <FiTrendingUp className="text-purple-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg GST %</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.avgGstPercentage}%
              </p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <FiPercent className="text-orange-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by banquet name, packages, price, or total..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : banquets.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No banquet packages found</h3>
            <p className="text-gray-600 mb-6">Get started by adding your first banquet bill</p>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPlus /> Add First Bill
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">#</span>
                        Bill No
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">👤</span>
                        Customer Details
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FiPackage />
                        Package & Banquet
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">₹</span>
                        Price/Pack
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">💰</span>
                        Amount Details
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {banquets.map((banquet) => (
                    <tr key={banquet._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-full">
                            <span className="text-blue-600 font-semibold text-sm">#</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{banquet.billNo}</div>
                            <div className="text-xs text-gray-500">Bill Number</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-purple-100 rounded-full">
                            <span className="text-purple-600 font-semibold text-sm">👤</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{banquet.customerName || 'N/A'}</div>
                            {banquet.gstNumber && (
                              <div className="text-xs text-blue-600 font-medium">GST: {banquet.gstNumber}</div>
                            )}
                            <div className="text-xs text-gray-500">Customer Details</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-full">
                            <span className="text-blue-600 font-semibold text-sm">{banquet.noOfPacks}</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{banquet.banquetName || 'N/A'}</div>
                            <div className="text-xs text-gray-600">Package #{banquet.noOfPacks}</div>
                            <div className="text-xs text-gray-500">Banquet & Package</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">₹{parseFloat(banquet.pricePerPack).toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Subtotal:</span>
                            <span className="text-sm text-gray-900">₹{parseFloat(banquet.subtotal).toFixed(2)}</span>
                          </div>
                          {banquet.discountAmount > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Discount:</span>
                              <span className="text-sm text-green-600">-₹{parseFloat(banquet.discountAmount).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">GST:</span>
                            <span className="text-sm text-blue-600">₹{parseFloat(banquet.gstAmount).toFixed(2)}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-1 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700">Total:</span>
                            <span className="text-sm font-bold text-blue-600">₹{parseFloat(banquet.total).toFixed(2)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {/* Download Bill */}
                          <button
                            onClick={() => handleDownloadBill(banquet)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition"
                          >
                            <FiFileText size={14} />
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Premium Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FiChevronLeft size={16} />
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-1 rounded-lg transition-all ${pageNum === pagination.page
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 hover:bg-white'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FiChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">
                {editingBanquet ? 'Edit Bill' : 'Add New Bill'}
              </h2>
              <button className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" onClick={() => {
                setShowModal(false);
                resetForm();
              }}>×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <span className="font-bold">👤</span> Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Enter customer name"
                  />
                </div>

                {/* GST NUMBER */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <span className="font-bold">🧾</span> GST Number
                  </label>
                  <input
                    type="text"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Enter GST number (e.g., 27ABCDE1234C1ZV)"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: 27ABCDE1234C1ZV (15 characters)</p>
                </div>
              </div>
                {/* CUSTOMER NAME */}
                

                {/* BANQUET NAME */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <span className="font-bold">📋</span> Banquet Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.banquetName}
                    onChange={(e) => setFormData({ ...formData, banquetName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Enter banquet name (e.g., Wedding Reception, Birthday Party)"
                  />
                </div>

                {/* PACK + PRICE */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FiPackage /> Number of Packs <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      required
                      min="1"
                      value={formData.noOfPacks}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setFormData({ ...formData, noOfPacks: value })
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                      placeholder="Example: 100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      ₹ Price per Pack <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.]*"
                      required
                      min="0"
                      step="0.01"
                      value={formData.pricePerPack}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Allow only one decimal point
                        const parts = value.split('.');
                        const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        setFormData({ ...formData, pricePerPack: cleanValue })
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                      placeholder="Example: 450"
                    />
                  </div>
                </div>

                {/* GST */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FiPercent /> GST Percentage <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.gstPercentage}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Allow only one decimal point
                      const parts = value.split('.');
                      const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      setFormData({ ...formData, gstPercentage: cleanValue })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Example: 18"
                  />
                </div>

                {/* DISCOUNT TYPE + DISCOUNT */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Type 
                    </label>
                    <select
                      value={formData.discountType}
                      onChange={(e) =>
                        setFormData({ ...formData, discountType: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (Rs)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount {formData.discountType === 'percentage' ? 'Percentage' : 'Amount'} 
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.]*"
                      required
                      min="0"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Allow only one decimal point
                        const parts = value.split('.');
                        const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        setFormData({ ...formData, discount: cleanValue })
                      }}
                      onBlur={(e) =>
                          setFormData({
                            ...formData,
                            discount: e.target.value === '' ? 0 : Number(e.target.value)
                          })
                        }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                      placeholder={`Enter ${formData.discountType === "percentage" ? "percentage" : "amount"
                        }`}
                    />
                  </div>
                </div>

                {/* PRICING PREVIEW */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Pricing Preview</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium text-gray-900">₹{totals.subtotal}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Discount ({formData.discountType === 'percentage' ? `${formData.discount}%` : 'Fixed'})</span>
                      <span className="font-medium text-green-600">-₹{totals.discountAmount}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Amount After Discount</span>
                      <span className="font-medium text-purple-600">₹{(parseFloat(totals.subtotal) - parseFloat(totals.discountAmount)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">GST ({formData.gstPercentage}%)</span>
                      <span className="font-medium text-blue-600">₹{totals.gstAmount}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-blue-50 px-4 rounded-lg">
                      <span className="font-semibold text-gray-800">Total</span>
                      <span className="font-bold text-blue-600 text-lg">₹{totals.total}</span>
                    </div>
                  </div>
                </div>

              </form>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors" onClick={() => {
                setShowModal(false);
                resetForm();
              }}>
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" onClick={handleSubmit}>
                {editingBanquet ? 'Update Bill' : 'Create Bill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BanquetManagement;
